import { useEffect, useState, VFC } from "react";
// Libraries
import { useHistory } from "react-router-dom";
// API
import { ImportResult_importResult } from "api/Import/CustomImport/types/ImportResult";
// Utils
import { ACTIVITIES_IMPORT, ANIMALS_IMPORT } from "constants/Import";
import { ANIMALS } from "constants/Routes";
import { useGetImportResult } from "hooks";
// Components
import { ImportWizardLoading } from "components/Import";
import { Button, Flex, Paper, Pill, Spacer, Text } from "components";

interface Hacky_ImportResult_importResult_errorRows_errors {
  code: string;
  field: string;
  message: string;
}

interface Hacky_ImportResult_importResult_errorRows {
  errors: Hacky_ImportResult_importResult_errorRows_errors[];
  rowIndex: number;
}

interface Hacky_ImportResult_importResult extends ImportResult_importResult {
  errorRows: Hacky_ImportResult_importResult_errorRows[];
}

interface ImportWizardResultProps {
  fileName: string;
  importType: typeof ANIMALS_IMPORT | typeof ACTIVITIES_IMPORT;
  onClickRestart: () => void;
  onSuccess: () => void;
  taskId: string;
}

export const ImportWizardResult: VFC<ImportWizardResultProps> = ({
  fileName,
  importType,
  onClickRestart,
  onSuccess,
  taskId,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [result, setResult] = useState<Hacky_ImportResult_importResult | null>(null);

  const { data } = useGetImportResult(taskId, { skip: !isLoading });

  useEffect(() => {
    if (data?.importResult && data.importResult.isDone) {
      setIsLoading(false);
      setResult(data.importResult);
      onSuccess();
    }
  }, [data]);

  const paperColour = isLoading ? undefined : "green";
  const fileNamePillCaption = `File: ${fileName}`;

  const hasFailedImports = result?.failedAnimalsUrl || result?.failedActivitiesUrl;
  const handleClickDownloadFailedAnimals = () => {
    if (result?.failedAnimalsUrl) {
      window.open(result?.failedAnimalsUrl, "Download");
    }
  };
  const handleClickDownloadFailedActivities = () => {
    if (result?.failedActivitiesUrl) {
      window.open(result?.failedActivitiesUrl, "Download");
    }
  };

  const history = useHistory();
  const handleClickProceed = () => history.push(ANIMALS);

  return (
    <>
      <Paper colour={paperColour} dark>
        <Flex container containerAlignItems="center" containerDirection="column">
          <Spacer baselineHeight={4} />

          {isLoading ? (
            <ImportWizardLoading text="Importing file" />
          ) : (
            <>
              <Text colour="green900">
                <strong>File imported</strong>
              </Text>

              <Spacer baselineHeight={1} />

              <Pill caption={fileNamePillCaption} colour="green" colourInverted />

              <Spacer baselineHeight={1} />

              <Text colour="green900" smallest>
                <strong>{importType === ANIMALS_IMPORT ? "Animals" : "Activities"} successfully imported</strong>
              </Text>
            </>
          )}

          <Spacer baselineHeight={4} />
        </Flex>
      </Paper>

      {!!result?.errorRows?.length && (
        <>
          <Spacer baselineHeight={2} />

          <Paper colour="red" dark>
            <Flex container containerAlignItems="center" containerDirection="column">
              <Spacer baselineHeight={4} />

              <Text colour="red900">
                <strong>Errors</strong>
              </Text>

              {result.errorRows.map(({ errors, rowIndex }) => (
                <>
                  <Spacer baselineHeight={1} />

                  {errors.map(({ message, field }) => (
                    <Text colour="red900" key={field} smallest>
                      <strong>
                        Row {rowIndex}: {field} - {message}
                      </strong>
                    </Text>
                  ))}
                </>
              ))}

              {hasFailedImports && (
                <>
                  <Spacer baselineHeight={1} />

                  <Flex container containerJustifyContent="center">
                    {result?.failedAnimalsUrl && (
                      <Flex item itemGutter>
                        <Button
                          caption="Download un-imported animals"
                          colour="grey"
                          onClick={handleClickDownloadFailedAnimals}
                          variant="hollow"
                        />
                      </Flex>
                    )}

                    {result?.failedActivitiesUrl && (
                      <Flex item itemGutter>
                        <Button
                          caption="Download un-imported activities"
                          colour="grey"
                          onClick={handleClickDownloadFailedActivities}
                          variant="hollow"
                        />
                      </Flex>
                    )}
                  </Flex>
                </>
              )}

              <Spacer baselineHeight={4} />
            </Flex>
          </Paper>
        </>
      )}

      <Spacer baselineHeight={4} />

      <Flex container containerJustifyContent="space-between">
        <Button caption="Start again" colour="grey" onClick={onClickRestart} variant="hollow" />

        <Button caption="Go to my animals" onClick={handleClickProceed} />
      </Flex>
    </>
  );
};
